import React, { useState, useEffect } from 'react';
import { Table, Button, Badge, Card, Row, Col, Form, InputGroup, Alert } from 'react-bootstrap';
import { Search, RefreshCw, Tag, Calendar, Package, ArrowUp, ArrowDown, Clipboard } from 'lucide-react';
import { getAllStockMovements } from '../../api/stock';
import useAuth from '../../hooks/useAuth';

const StockMovementList = ({ onAddMovement }) => {
    const { isAdmin, isManager, isWarehouseManager } = useAuth();
    const [movements, setMovements] = useState([]);
    const [filteredMovements, setFilteredMovements] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [refreshTrigger, setRefreshTrigger] = useState(0);
    const [selectedType, setSelectedType] = useState('ALL');

    const canAddMovement = isAdmin || isManager || isWarehouseManager;

    useEffect(() => {
        const fetchMovements = async () => {
            try {
                setLoading(true);
                setError('');
                const data = await getAllStockMovements();

                const sortedData = [...data].sort((a, b) => {
                const id1 = Number(a.id);
                const id2 = Number(b.id);
                return id1 - id2;
            });
                setMovements(sortedData);
                setFilteredMovements(sortedData);
            } catch (err) {
                setError('Failed to load stock movements. Please try again.');
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        fetchMovements();
    }, [refreshTrigger]);

    useEffect(() => {
        let filtered = movements;

        // Filter by type
        if (selectedType !== 'ALL') {
            filtered = filtered.filter(movement => movement.type === selectedType);
        }

        // Filter by search term
        if (searchTerm.trim() !== '') {
            filtered = filtered.filter(movement =>
                movement.product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                movement.product.sku.toLowerCase().includes(searchTerm.toLowerCase()) ||
                (movement.reference && movement.reference.toLowerCase().includes(searchTerm.toLowerCase()))
            );
        }

        setFilteredMovements(filtered);
    }, [searchTerm, movements, selectedType]);

    const handleRefresh = () => {
        setRefreshTrigger(prev => prev + 1);
    };

    const getTypeIcon = (type) => {
        switch (type) {
            case 'STOCK_IN':
                return <ArrowUp size={16} className="text-success" />;
            case 'STOCK_OUT':
                return <ArrowDown size={16} className="text-danger" />;
            case 'ADJUSTMENT':
                return <Clipboard size={16} className="text-warning" />;
            case 'RETURN':
                return <ArrowUp size={16} className="text-info" />;
            default:
                return <Package size={16} />;
        }
    };

    const getTypeBadge = (type) => {
        switch (type) {
            case 'STOCK_IN':
                return <Badge bg="success">Entrée en stock</Badge>;
            case 'STOCK_OUT':
                return <Badge bg="danger">Sortie de stock</Badge>;
            case 'ADJUSTMENT':
                return <Badge bg="warning">Ajustement</Badge>;
            case 'RETURN':
                return <Badge bg="info">Retours</Badge>;
            default:
                return <Badge bg="secondary">{type}</Badge>;
        }
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('fr-FR', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <Card className="shadow-sm">
            <Card.Header>
                <Row className="align-items-center">
                    <Col>
                        <h5 className="mb-0">Historique des mouvements de stock</h5>
                    </Col>
                    <Col xs="auto">
                        {canAddMovement && (
                            <Button
                                variant="success"
                                size="sm"
                                className="me-2"
                                onClick={onAddMovement}
                            >
                                <Package size={16} className="me-1" />
                                Ajouter mouvement
                            </Button>
                        )}
                        <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={handleRefresh}
                        >
                            <RefreshCw size={16} />
                        </Button>
                    </Col>
                </Row>
            </Card.Header>
            <Card.Body>
                {error && <Alert variant="danger">{error}</Alert>}

                <Row className="mb-3">
                    <Col md={8}>
                        <InputGroup>
                            <InputGroup.Text>
                                <Search size={18} />
                            </InputGroup.Text>
                            <Form.Control
                                placeholder="Rechercher par reférence ou produit..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </InputGroup>
                    </Col>
                    <Col md={4}>
                        <Form.Select
                            value={selectedType}
                            onChange={(e) => setSelectedType(e.target.value)}
                        >
                            <option value="ALL">Tous les types de mouvements</option>
                            <option value="STOCK_IN">Entrée en stock </option>
                            <option value="STOCK_OUT">Sortie de stock</option>
                            <option value="ADJUSTMENT">Ajustments de stock</option>
                            <option value="RETURN">Retours</option>
                        </Form.Select>
                    </Col>
                </Row>

                {loading ? (
                    <div className="text-center py-4">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Chargement...</span>
                        </div>
                        <p className="mt-2">Chargement des mouvements de stocks...</p>
                    </div>
                ) : (
                    <div className="table-responsive">
                        <Table striped hover>
                            <thead>
                                <tr>
                                    <th>Date</th>
                                    <th>N° Produit</th>
                                    <th>Produit</th>
                                    <th>Type</th>
                                    <th>Quantité</th>
                                    {/* <th>Référence</th> */}
                                    <th>Effectué par</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredMovements.length > 0 ? (
                                    filteredMovements.map(movement => (
                                        <tr key={movement.id}>
                                            <td>
                                                <div className="small d-flex align-items-center">
                                                    <Calendar size={12} className="me-1" />
                                                    {formatDate(movement.movementDate)}
                                                </div>
                                            </td>
                                            <td>{movement.product.numeroProduit ? movement.product.numeroProduit : null }</td>
                                            <td>
                                                <div className="fw-bold">{movement.product.name}</div>
                                                <div className="small d-flex align-items-center">
                                                    <Tag size={12} className="me-1" />
                                                    {movement.product.sku}
                                                </div>
                                            </td>
                                            <td>
                                                <div className="d-flex align-items-center">
                                                    {getTypeIcon(movement.type)}
                                                    <span className="ms-2">{getTypeBadge(movement.type)}</span>
                                                </div>
                                            </td>
                                            <td className="fw-bold">
                                                {movement.type === 'STOCK_OUT' ? '-' : '+'}{movement.quantity}
                                            </td>
                                            {/* <td>{movement.reference || 'N/A'}</td> */}
                                            <td>{movement.performedBy ? movement.performedBy.fullName : 'System'}</td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="6" className="text-center py-3">
                                            Aucune ligne de mouvement de stock trouvée
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </Table>
                    </div>
                )}
            </Card.Body>
        </Card>
    );
};

export default StockMovementList;